// src/utils/funnelTracker.js
//
// Captures the `?source=` query param on first page load and persists it
// to localStorage so it survives the Firebase email-verify redirect and any
// subsequent navigation before signup completes.
//
// Backend (`ensureUserRecord`) consumes the value exactly once on the first
// authenticated call — see backend/middleware/ensureUserRecord.js. Frontend
// just needs to capture, persist, and (after auth) PATCH it to the profile
// endpoint via AuthContext.

const STORAGE_KEY = 'bm_entry_funnel';
const VALID_FUNNELS = ['voice', 'browser', 'chats', 'web', 'extension', 'direct'];

/**
 * Read the funnel from `?source=` on the current URL (if present + valid)
 * and persist to localStorage. Called once at app boot. Idempotent — if a
 * funnel is already persisted, only OVERWRITES when a new explicit ?source=
 * is in the URL (so an inbound ad link can override a stale cached value).
 */
export function captureFunnelFromUrl() {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = (params.get('source') || '').toLowerCase().trim();
    if (raw && VALID_FUNNELS.includes(raw)) {
      window.localStorage.setItem(STORAGE_KEY, raw);
      return raw;
    }
    // No ?source= on the URL — return whatever's already cached (may be null).
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Read the persisted funnel without touching the URL. */
export function getPersistedFunnel() {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v && VALID_FUNNELS.includes(v) ? v : null;
  } catch {
    return null;
  }
}

/**
 * Clear the persisted funnel. Call after the backend confirms it was
 * consumed (or after the user has been on the site for a while and the
 * cohort has been recorded).
 */
export function clearPersistedFunnel() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch { /* noop */ }
}

/** Map funnel → in-app landing route. */
export function getLandingRouteForFunnel(funnel) {
  switch (funnel) {
    case 'voice':   return '/voice';
    case 'browser': return '/browser';
    case 'chats':   return '/chats';
    default:        return null; // no override — use existing default (/sessions)
  }
}

export const FUNNEL_STORAGE_KEY = STORAGE_KEY;
export const VALID_FUNNEL_VALUES = VALID_FUNNELS;
